import AddIcon from '@mui/icons-material/Add';
import { Button, Paper, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import RebateCard from './RebateCard';
import { getListOfPeriods, getPeriodName, makePeriod } from '../../utils/rebatePeriods';

const makeRebate = () => ({ name: '', startDate: '', endDate: '', programType: '', rebateValueUnit: 0, periodType: '', description: '', periods: [] });

const normalizeInput = (input) => String(input ?? '').toLowerCase();

const REBATE_VALUE_UNIT_OPTIONS = {
    flat_rebate_value: 'flat_rebate_amount',
    percentage_spend: 'percentage_spend',
    per_unit_rebate_value: 'per_unit_rebate_value',
};

function EditRebates({ contractData, rebatesData, parsedData, onBack, onSave, onSkip }) {
    const nextId = useRef(1);
    const withIds = (rebates) => rebates.map(r => r._id != null ? r : { ...r, _id: nextId.current++ });

    const rebatesFromParsedData = parsedData?.contract.rebate_groups ?? [];
    const mappedRebates = rebatesFromParsedData.map((rebate) => {
        const rebateValueField = REBATE_VALUE_UNIT_OPTIONS[rebate.rebate_value_unit] ?? 'flat_rebate_amount';
        const rawPeriods = rebate.periods?.length ? rebate.periods : [];

        const periodList = rawPeriods.flatMap((period) => {
            const tiers = (period.rules?.tiers ?? []).map((tier) => ({
                threshold: tier.threshold ?? '',
                rebateValue: tier[rebateValueField] ?? '',
            }));
            const formulaDescription = period.rules?.rule_description ?? '';
            const subPeriods = getListOfPeriods(period.start_date, period.end_date, rebate.period_type);
            const rows = subPeriods.length ? subPeriods : [{ startDate: period.start_date ?? '', endDate: period.end_date ?? '' }];

            return rows.map((row) => makePeriod({
                startDate: row.startDate ?? period.start_date ?? '',
                endDate: row.endDate ?? period.end_date ?? '',
                formulaDescription,
                tiers,
            }));

        }).map((period, index) => ({
            ...period,
            name: getPeriodName(rebate.period_type, index),
        }));
        
        return {
            name: rebate.rebate_name ?? '',
            startDate: rebate.start_date ?? '',
            endDate: rebate.end_date ?? '',
            programType: normalizeInput(rebate.rebate_program_type),
            rebateValueUnit: rebate.rebate_value_unit ?? '',
            periodType: rebate.period_type ?? '',
            description: rebate.text_description ?? '',
            periods: periodList
        };

    });

    const [rebates, setRebates] = useState(() => withIds(
        rebatesData?.length ? rebatesData : (mappedRebates.length ? mappedRebates : [makeRebate()])
    ));

    const handleChange = (id, updated) => {
        setRebates(prev => prev.map(r => r._id === id ? updated : r));
    };

    const handleDelete = (id) => {
        setRebates(prev => prev.filter(r => r._id !== id));
    };

    const handleAdd = () => {
        setRebates(prev => withIds([...prev, makeRebate()]));
    };

    const handleSave = async () => {
        const data = rebates.map(({ _id, ...rest }) => rest);
        await onSave(data);
    };

    return (
        <Stack spacing={3} sx={{ maxWidth: 800, mx: 'auto', mt: 4, pb: 8 }}>
            <Stack spacing={0.5}>
                <Typography variant='heavy' sx={{ fontSize: '24px' }}>
                    Configure Rebates
                </Typography>
                <Typography sx={{ color: 'subtext.main', fontSize: '14px' }}>
                    Add rebates associated with{contractData?.name ? ` ${contractData.name}` : ' this contract'}. A contract can have multiple rebates.
                </Typography>
            </Stack>

            {rebates.map((rebate) => (
                <RebateCard
                    key={rebate._id}
                    rebate={rebate}
                    onChange={(updated) => handleChange(rebate._id, updated)}
                    onDelete={() => handleDelete(rebate._id)}
                />
            ))}

            {/* Add Rebate */}
            <Paper
                elevation={0}
                onClick={handleAdd} role='button' tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAdd(); } }}
                sx={{
                    border: '2px dashed',
                    borderColor: 'background.disabled',
                    borderRadius: 2,
                    p: 2.5,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'blue.main', backgroundColor: 'background.green' },
                    transition: 'border-color 0.2s, background-color 0.2s',
                }}
            >
                <Stack direction='row' spacing={1} justifyContent='center' alignItems='center'>
                    <AddIcon sx={{ fontSize: 18, color: 'subtext.main' }} />
                    <Typography sx={{ fontSize: '14px', color: 'subtext.main' }}>Add Rebate</Typography>
                </Stack>
            </Paper>

            {/* Footer actions */}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Button variant='text' color='inherit' sx={{ color: 'subtext.main' }} onClick={onBack}>
                    ← Back
                </Button>
                <Stack direction='row' spacing={2}>
                    {onSkip && (
                        <Button variant='text' color='inherit' sx={{ color: 'subtext.main' }} onClick={onSkip}>
                            Skip for now
                        </Button>
                    )}
                    <Button variant='blueContained' onClick={handleSave}>
                        Save Rebates
                    </Button>
                </Stack>
            </Stack>
        </Stack>
    );
}

export default EditRebates;

